/**
 * 63.12 (DECISIONS 243, 244; ARCHITECTURE §5.24), with GameConfig.vehicleZones: a vehicle hit by where it is hit. Each hull's
 * zones are regions of its body's frame (x across, y up, z forward, from its feet, in people — constants/hitboxes.ts's frame),
 * the first that holds a hit point its zone (none: `hull`); each zone a mocked armour rating (0–10, one material: the owner's
 * "material can be held to one"); each seat the zones it sits behind. A round or a burst brings a penetration in the same
 * units. The owner's picks (244): the zones from the hit point (no new boxes yet — core/vehicleZones.ts zoneOf is the one door
 * a fitted set can later back), no angle and no parts yet (with the tank), a gunfire kill burns out, the load by √ capped, and
 * these values as the starting table (6a) — proposals to tune in play.
 */
import type { CartridgeKind } from '../types/player';
import type { ExplosionClass } from './explosions';

export type VehicleZone = 'glass' | 'door' | 'engine' | 'tail' | 'hull' | 'top' | 'running' | 'front';   // (69.29: the front, the heavy class's own face)
/** The kinds the zones cover (the bike is open: its rider is shot as a person; the walker and the guns keep today's rule). */
export type ZonedVehicle = 'technical' | 'supplyTruck' | 'wheeled' | 'tracked' | 'tank';
export type VehicleClass = 'soft' | 'light' | 'armoured' | 'heavy';

export interface ZoneRegion { zone: VehicleZone; min: readonly [number, number, number]; max: readonly [number, number, number] }
export interface ZoneTable {
  cls: VehicleClass;
  /** Checked in order; the first region holding the point is its zone, else `hull`. */
  regions: readonly ZoneRegion[];
  rating: Readonly<Record<VehicleZone, number>>;
  /** Per seat (BODIES' order): the zones it sits behind, and where in the frame (a bay seat has no point of its own: the bay's). */
  seats: ReadonlyArray<{ zones: readonly VehicleZone[]; at: readonly [number, number, number] }>;
  /**
   * 69.29: a turret's own zones — a point at or over `fromY` within its box (`lo`–`hi`, in the turret's frame: turned by the
   * structure's turretYaw about the axis at z `pivotZ`) takes the first of `regions` holding it there, else the turret's sides (`hull`).
   */
  turret?: { fromY: number; pivotZ: number; lo: readonly [number, number, number]; hi: readonly [number, number, number]; regions: readonly ZoneRegion[] };
}

const NONE = [-99, -99, -99] as const, ALL = [99, 99, 99] as const;
/** The cab of a pickup (the technical, the supply truck): its doors to the window line, its glass above, the bonnet ahead, the bed behind. */
const pickup = (cabZ0: number, cabZ1: number): ZoneRegion[] => [
  { zone: 'running', min: NONE, max: [99, 0.18, 99] },
  { zone: 'engine', min: [-99, -99, cabZ1], max: ALL },
  { zone: 'tail', min: NONE, max: [99, 99, cabZ0] },
  { zone: 'glass', min: [-99, 0.72, cabZ0], max: [99, 99, cabZ1] },
  { zone: 'door', min: [-99, -99, cabZ0], max: [99, 0.72, cabZ1] },
];

export const VEHICLE_ZONES: Record<ZonedVehicle, ZoneTable> = {
  // 63.1o: the technical — the driver's door on its left (+x), the passenger's on its right, the gunner standing in the bed
  technical: {
    cls: 'soft',
    regions: pickup(-0.234, 0.678),
    rating: { glass: 0, door: 0, engine: 2, tail: 0, hull: 1, top: 0, running: 1, front: 2 },
    seats: [{ zones: ['door', 'glass'], at: [0.25, 0.417, 0.306] }, { zones: ['tail'], at: [0, 0.494, -0.972] }, { zones: ['door', 'glass'], at: [-0.25, 0.417, 0.306] }],
  },
  supplyTruck: {
    cls: 'soft',
    regions: pickup(-0.042, 0.678),
    rating: { glass: 0, door: 0, engine: 2, tail: 0, hull: 1, top: 0, running: 1, front: 2 },
    seats: [{ zones: ['door', 'glass'], at: [0.25, 0.417, 0.306] }, { zones: ['door', 'glass'], at: [-0.25, 0.417, 0.306] }],
  },
  // 63.1d: the 6×6 — the engine forward, the vision blocks above it, the ramp behind, the roof and the turret on top
  wheeled: {
    cls: 'light',
    regions: [
      { zone: 'running', min: NONE, max: [99, 0.35, 99] },
      { zone: 'top', min: [-99, 1.2, -99], max: ALL },
      { zone: 'glass', min: [-99, 0.95, 1.1], max: ALL },
      { zone: 'engine', min: [-99, -99, 1.1], max: ALL },
      { zone: 'tail', min: NONE, max: [99, 99, -1.3] },
    ],
    rating: { glass: 2, door: 4, engine: 4, tail: 3, hull: 4, top: 2, running: 3, front: 5 },
    seats: [
      { zones: ['glass', 'engine'], at: [-0.306, 0.633, 0.694] }, { zones: ['top'], at: [0.194, 0.816, -0.167] },
      { zones: ['tail', 'hull'], at: [-0.35, 0.6, -1.0] }, { zones: ['tail', 'hull'], at: [0.35, 0.6, -1.0] },
      { zones: ['tail', 'hull'], at: [-0.35, 0.6, -0.4] }, { zones: ['tail', 'hull'], at: [0.35, 0.6, -0.4] },
    ],
  },
  // 197: the tracked carrier — no glass (its driver under a hatch), the engine forward, the ramp behind
  tracked: {
    cls: 'armoured',
    regions: [
      { zone: 'running', min: NONE, max: [99, 0.35, 99] },
      { zone: 'top', min: [-99, 1.05, -99], max: ALL },
      { zone: 'engine', min: [-99, -99, 0.9], max: ALL },
      { zone: 'tail', min: NONE, max: [99, 99, -1.0] },
    ],
    rating: { glass: 3, door: 5, engine: 5, tail: 4, hull: 5, top: 3, running: 4, front: 6 },
    seats: [
      { zones: ['top', 'engine'], at: [0.333, 0.489, 0.722] }, { zones: ['top'], at: [-0.194, 0.617, 0.194] }, { zones: ['top'], at: [0.222, 0.472, -0.889] },
      { zones: ['tail', 'hull'], at: [-0.35, 0.55, -1.0] }, { zones: ['tail', 'hull'], at: [0.35, 0.55, -1.0] },
      { zones: ['tail', 'hull'], at: [-0.35, 0.55, -0.4] }, { zones: ['tail', 'hull'], at: [0.35, 0.55, -0.4] },
    ],
  },
  // 69.29 step 4 (§5.25 (b); lane E's fit, hitShapeFit fitTank): the tank — the heavy class. The running gear low down each
  // side and the engine at the rear's middle are acting parts (a penetrating hit there: a mobility kill, core/vehicleZones.ts);
  // the front the thickest; the turret its own zones in its turned frame (its front, its rear, its top; its sides `hull`)
  tank: {
    cls: 'heavy',
    regions: [
      { zone: 'running', min: [0.52, -99, -99], max: [99, 0.534, 99] },
      { zone: 'running', min: [-99, -99, -99], max: [-0.52, 0.534, 99] },
      { zone: 'top', min: [-99, 0.78, -99], max: ALL },
      { zone: 'front', min: [-99, -99, 1.4], max: ALL },
      { zone: 'engine', min: [-0.472, 0.25, -99], max: [0.472, 99, -1.139] },
      { zone: 'tail', min: NONE, max: [99, 99, -1.4] },
    ],
    rating: { glass: 10, door: 10, engine: 7, tail: 6, hull: 7, top: 4, running: 6, front: 10 },
    seats: [{ zones: ['front', 'top'], at: [0.267, 0.4, 0.583] }, { zones: ['hull', 'front', 'top'], at: [0.25, 1.0, -0.444] }, { zones: ['top'], at: [-0.25, 1.3, -0.389] }],   // (247: the commander up in the cupola, the gunner in by the right hatch)
    turret: { fromY: 0.79, pivotZ: -0.194, lo: [-0.531, 0.792, -1.027], hi: [0.531, 1.524, 0.489], regions: [
      { zone: 'top', min: [-99, 1.45, -99], max: ALL },
      { zone: 'front', min: [-99, -99, 0.33], max: ALL },
      { zone: 'tail', min: NONE, max: [99, 99, -0.87] },
    ] },
  },
};

/** What a round brings against a zone's rating (a cartridge absent: a rifle's). */
export const ROUND_PENETRATION: Partial<Record<CartridgeKind, number>> = { '9mm': 0.5, '.45': 0.5, '5.56': 1, '7.62': 1, '.30-06': 1.5, '.50': 3 };
/** What a burst brings (the anti-dropship missile bursts as the rocket's class against the ground). 69.29 (DECISIONS 247, the owner: "I don't think we want the panzerfaust to be better than the law for frontal"): the heavier tube's the rocket's 7 (248: "let's just make it identical for now" — its burst the LAW's too, constants/explosions.ts). */
export const BURST_PENETRATION: Record<ExplosionClass, number> = { law: 7, gl40: 4, panzerfaust: 7, handGrenade: 2, strikeCharge: 3, bomb: 6, shell: 3, heavyShell: 5, vehicleDeath: 3, tankHe: 6, cannon20: 2, charge: 9 };   // (69.47: the charge's 9 beats every rating but a tank's front, 10)

export const ZONE_RULES = {
  /** A penetrating burst kills outright the occupant of a seat behind the zone it came through within this reach of the hit (people). */
  outrightPeople: 0.65,
  /** What of a penetrating burst reaches the rest inside, by zone (the engine block and the running gear take most of it). */
  passShare: { glass: 1, door: 1, engine: 0.25, tail: 1, hull: 1, top: 1, running: 0.5, front: 1 } as Readonly<Record<VehicleZone, number>>,
  /**
   * 69.29 step 4 (DECISIONS 246, the owner: "cumulative damage … the front should be able to tank a bunch of rounds, from the
   * side 2 rounds at least"): on the armoured and heavy classes a hit wears the hull by its margin `m` against the zone's angled
   * rating (pen − angledRating) — its blow × clamp(base + perMargin·m, floor, 1), whether it gets through or not (through:
   * the occupants' outcomes too). A rocket: about 9 from a tank's front, 2 on a side, 2 at the tail.
   */
  wear: { base: 0.72, perMargin: 0.185, floor: 0.1 },   // (a burst's falloff a step off the hull — 0.95 — took a square side's 2 to 3 at 0.67)
  /** A burst the zone stops: the share of its blow the hull still takes (its plates dented). */
  stoppedBurst: 0.1,
  /** A penetrating round that finds no one behind its zone, on a hull no round wears (`hull` material): what it takes off the health (the soft row's share). */
  hullRound: 0.15,
  /** A gunfire kill burns out: the burst `delayS` later, its radius × `radiusShare`; everyone aboard bails out first. */
  burnOut: { delayS: 3, radiusShare: 1 / 3 },
  /** The load (a launcher's rounds aboard) widens the death burst: × (1 + `perRoot` · √rounds), at most `cap` (the owner: "maybe not directly"). */
  load: { perRoot: 0.15, cap: 1.6 },
} as const;
